import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addPlatformCredits, fetchPlatformOverview, listUserCredits, PlatformOverview } from '../../services/CreditsService';

interface CreditAllocation {
  client: string;
  allocated: number;
  used: number;
  remaining: number;
  plan: string;
}

const AddCreditsPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [creditsToAdd, setCreditsToAdd] = useState<number>(0);
  const [provider, setProvider] = useState<string>('');
  const [purchaseReference, setPurchaseReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [usageFilter, setUsageFilter] = useState<string>('all');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [rows, setRows] = useState<Array<{ client: string; allocated: number; used: number; remaining: number; plan: string }>>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const ov = await fetchPlatformOverview();
        setOverview(ov);
        const list = await listUserCredits();
        setRows(list.map((r) => ({ client: r.client, allocated: r.allocated, used: r.used, remaining: r.remaining, plan: r.plan || '—' })));
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const handleSaveCredits = async () => {
    try {
      await addPlatformCredits({ amount: creditsToAdd, provider, purchaseReference, notes });
      const ov = await fetchPlatformOverview();
      setOverview(ov);
      setSuccessMsg(`Successfully added ${creditsToAdd.toLocaleString()} credits to the platform.`);
      setErrorMsg('');
      setTimeout(() => setSuccessMsg(''), 4000);
      setCreditsToAdd(0);
      setProvider('');
      setPurchaseReference('');
      setNotes('');
    } catch (e) {
      console.error(e);
      setSuccessMsg('');
      setErrorMsg(e instanceof Error ? e.message : 'Failed to add credits');
      setTimeout(() => setErrorMsg(''), 5000);
    }
  };

  const filteredAllocations = useMemo(() => {
    let data = rows;
    if (clientFilter !== 'all') data = data.filter((r) => r.client === clientFilter);
    // plan not implemented on backend yet
    if (usageFilter !== 'all') {
      data = data.filter((r) => {
        const pct = r.allocated > 0 ? r.used / r.allocated : 0;
        if (usageFilter === 'high') return pct > 0.75;
        if (usageFilter === 'medium') return pct >= 0.25 && pct <= 0.75;
        return pct < 0.25;
      });
    }
    return data;
  }, [rows, clientFilter, usageFilter, planFilter]);

  const clientOptions = useMemo(() => ['all', ...rows.map((r) => r.client)], [rows]);

  const handleGenerateReport = async () => {
    try {
      const list = await listUserCredits({ client: clientFilter === 'all' ? undefined : clientFilter, plan: planFilter === 'all' ? undefined : planFilter });
      setRows(list.map((r) => ({ client: r.client, allocated: r.allocated, used: r.used, remaining: r.remaining, plan: r.plan || '—' })));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {successMsg ? (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
          {successMsg}
        </div>
      ) : null}
      {errorMsg ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
          {errorMsg}
        </div>
      ) : null}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Add Platform Credits</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/billing')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveCredits}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Save Credits
          </button>
        </div>
      </div>

      {/* Section 1: Current Credit Balance */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Current Credit Balance</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Credits</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{(overview?.total || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Platform total</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Allocated Credits</h3>
            <div className="text-2xl font-bold text-blue-600">{(overview?.allocated || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Assigned to clients</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Available Credits</h3>
            <div className="text-2xl font-bold text-green-600">{(overview?.available || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Ready to allocate</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Pending Credits</h3>
            <div className="text-2xl font-bold text-yellow-600">{(overview?.pending || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Awaiting confirmation</p>
          </div>
        </div>
      </div>

      {/* Section 2: Add Credits */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Add Credits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Credits
            </label>
            <input
              type="number"
              value={creditsToAdd}
              onChange={(e) => setCreditsToAdd(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Enter number of credits"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Network Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select provider...</option>
              <option value="econet">Econet</option>
              <option value="netone">NetOne</option>
              <option value="telecel">Telecel</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purchase Reference
            </label>
            <input
              type="text"
              value={purchaseReference}
              onChange={(e) => setPurchaseReference(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Enter purchase reference number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              rows={1}
              placeholder="Add any additional notes..."
            />
          </div>
        </div>
      </div>

      {/* Section 3: Credits Allocation Overview */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Credits Allocation Overview</h2>
        </div>

        {/* Filters Row */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-4">
            <div>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              >
                <option value="all">All Clients</option>
                {clientOptions.map((opt, index) => (
                  <option key={index} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              >
                <option value="all">All Plans</option>
                <option value="Enterprise">Enterprise</option>
                <option value="Professional">Professional</option>
                <option value="Basic">Basic</option>
              </select>
            </div>

            <div>
              <select
                value={usageFilter}
                onChange={(e) => setUsageFilter(e.target.value)}
                className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              >
                <option value="all">All Usage</option>
                <option value="high">High Usage (&gt;75%)</option>
                <option value="medium">Medium Usage (25-75%)</option>
                <option value="low">Low Usage (&lt;25%)</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleGenerateReport}
            className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
          >
            Generate
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allocated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAllocations.map((allocation, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{allocation.client}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{allocation.plan}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{allocation.allocated.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{allocation.used.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{allocation.remaining.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-grow h-2 bg-gray-200 rounded-full mr-2">
                        <div 
                          className="h-2 bg-[#FF5722] rounded-full" 
                          style={{ width: `${(allocation.used / allocation.allocated) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">
                        {((allocation.used / allocation.allocated) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AddCreditsPage; 